import {
  Box,
  MenuItem,
  RadioGroup,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import RadioBox from '$components/RadioBox';
import {FormikErrors, FormikTouched} from 'formik';
import {PatientInsuranceDetailFormData} from './schema';
import styles from '../../styles';
import SelectInput from '$components/SelectInput';
import {useQuery} from '@tanstack/react-query';
import Loading from '$components/Loading';
import FetchError from '$components/FetchError';
import {getInsuranceTypes, getPaymentTypes} from '$api/bookingFlow';
import Input from '$components/Input';
import {Insurance, Payment, PaymentMode} from '../../types';
import PaymentSummaryBox from '../PaymentSummaryBox';
import {usePaymentAmount} from '../../hooks';
import {StripeWidgetRef} from '../StripeWidget';
import React from 'react';
import StripeWidget from '../StripeWidget';
import {PLACEHOLDERS, LABELS, TEST_IDS} from './constants';
import AlternatePaymentIcon from '$assets/svgs/alternate-payment.svg';

import {
  formBoxStyles,
  questionBoxStyles,
  insuranceDetailsBoxStyles,
  selectInputBoxStyles,
  InsuranceDescription,
} from './style';

interface PatientInsuranceDetailFormProps {
  values: PatientInsuranceDetailFormData & {
    consultant?: {
      id?: number;
    };
  };
  errors: FormikErrors<PatientInsuranceDetailFormData>;
  touched: FormikTouched<PatientInsuranceDetailFormData>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
  handleChange: React.ChangeEventHandler<HTMLInputElement>;
  setFieldTouched: (field: string) => void;
  setFieldValue: (field: string, value: unknown) => void;
  setValues: (values: PatientInsuranceDetailFormData) => void;
  stripeWidgetRef: React.RefObject<StripeWidgetRef>;
  onPayPalClick?: () => void;
  // Add bookingChoices if available in props
  bookingChoices?: {
    patientCarerDetails?: {
      patient?: {
        country?: string;
      };
    };
    currency?: string;
  };
}

function PatientInsuranceDetailForm(props: PatientInsuranceDetailFormProps) {
  const {
    values,
    setFieldValue,
    setValues,
    handleBlur,
    touched,
    errors,
    handleChange,
    stripeWidgetRef,
    // Add bookingChoices if available in props
    bookingChoices,
  } = props;
  const selfPaying = values.selfPaying;
  const consultantId = values.consultant?.id?.toString();

  const {data: amount, isLoading: isAmountLoading} = usePaymentAmount(
    consultantId || ''
  );

  const {data, isLoading} = useQuery({
    queryKey: selfPaying ? ['payment-types'] : ['insurance-types'],
    queryFn: selfPaying ? getPaymentTypes : getInsuranceTypes,
  });

  const handlePaymentMethodChange = React.useCallback(
    (value: string) => {
      if (!data) return;
      const items = data.data;

      if (!value) {
        if (selfPaying && values.paymentMode !== '') {
          setFieldValue('paymentMode', '');
        } else if (
          !selfPaying &&
          values.insuranceId !== undefined &&
          !isNaN(Number(values.insuranceId))
        ) {
          setFieldValue('insuranceId', NaN);
        }
        return;
      }

      const item = items.find(it =>
        selfPaying
          ? it.name === value
          : it.id !== undefined && it.id.toString() === value
      );

      if (item) {
        if (selfPaying && values.paymentMode !== item.name) {
          setFieldValue('paymentMode', item.name);
        } else if (!selfPaying) {
          const newValue = item.id !== undefined ? Number(item.id) : NaN;
          if (values.insuranceId !== newValue) {
            setFieldValue('insuranceId', newValue);
          }
        }
      } else {
        if (selfPaying && values.paymentMode !== '') {
          setFieldValue('paymentMode', '');
        } else if (
          !selfPaying &&
          values.insuranceId !== undefined &&
          !isNaN(Number(values.insuranceId))
        ) {
          setFieldValue('insuranceId', NaN);
        }
      }
    },
    [selfPaying, data, values.paymentMode, values.insuranceId, setFieldValue]
  );

  const renderMenuItem = React.useCallback(
    (item: Insurance | PaymentMode) => {
      return (
        <MenuItem
          key={item.id !== undefined ? item.id : item.name}
          value={
            selfPaying
              ? item.name
              : item.id !== undefined
                ? item.id.toString()
                : ''
          }
        >
          <Typography variant="subtitle1">{item.name}</Typography>
        </MenuItem>
      );
    },
    [selfPaying]
  );

  const renderValue = React.useCallback(
    (value: string) => {
      if (!data) return '';
      const items = data.data;

      const placeholder = selfPaying
        ? PLACEHOLDERS.PAYMENT_METHOD
        : PLACEHOLDERS.INSURANCE_NAME;

      if (!value) return placeholder;

      const item = items.find(it =>
        selfPaying
          ? it.name === value
          : it.id !== undefined && it.id.toString() === value
      );

      if (!item) return placeholder;

      return item.name;
    },
    [selfPaying, data]
  );

  if (isLoading || isAmountLoading) return <Loading />;
  if (!data) return <FetchError />;

  const items: Insurance[] | Payment[] = data.data;

  const handleSelfPayingChange: React.ChangeEventHandler<
    HTMLInputElement
  > = e => {
    const value = e.target.value;

    const selfPaying =
      value === 'true' ? true : value === 'false' ? false : values.selfPaying;

    setValues({
      ...values,
      selfPaying,
      paymentMode: selfPaying === true ? 'Card' : '',
      insuranceId: NaN,
      membershipNumber: '',
      preAuthCode: '',
      acceptResponsibility: false,
    });
  };

  // Extract country from bookingChoices if available
  let country = 'GB';
  let currency = 'GBP';
  if (
    bookingChoices &&
    bookingChoices.patientCarerDetails &&
    bookingChoices.patientCarerDetails.patient &&
    bookingChoices.patientCarerDetails.patient.country
  ) {
    country = bookingChoices.patientCarerDetails.patient.country;
  }
  currency = bookingChoices?.currency || 'GBP';

  return (
    <>
      <Box sx={questionBoxStyles}>
        <Typography variant="h3" sx={styles.stepsTypeHead}>
          {LABELS.SELF_PAYING_QUESTION}
        </Typography>
        <RadioGroup
          row
          sx={{display: 'flex', gap: '2rem'}}
          onChange={handleSelfPayingChange}
          value={selfPaying === null ? '' : String(selfPaying)}
        >
          <RadioBox
            value={String(true)}
            label={LABELS.YES}
            data-testid={TEST_IDS.SELF_PAYING_YES}
          />
          <RadioBox
            value={String(false)}
            label={LABELS.NO}
            data-testid={TEST_IDS.SELF_PAYING_NO}
          />
        </RadioGroup>
      </Box>
      {selfPaying !== null && (
        <PaymentSummaryBox
          totalAmount={amount || 0}
          items={[{label: 'Surgery consultation', amount: amount || 0}]}
          currency="£"
          headerText={'Total to pay'}
        />
      )}
      {selfPaying !== null && (
        <Box sx={formBoxStyles}>
          {selfPaying === true ? (
            <>
              <StripeWidget
                ref={stripeWidgetRef}
                onPayPalClick={props.onPayPalClick}
                className="stripe-widget"
                amount={amount ? Math.round(Number(amount) * 100) : 0} // convert to pence
                currency={currency}
                country={country}
              />
              <Box
                mt={4}
                p={2}
                borderRadius={2}
                bgcolor="#f5f7ff"
                display="flex"
                alignItems="flex-start"
                gap={2}
              >
                <Box>
                  <img
                    src={AlternatePaymentIcon}
                    alt="Alternate Payment"
                    width={56}
                    height={56}
                  />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {LABELS.NEED_ALTERNATIVE_PAYMENT}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    fontWeight={400}
                  >
                    {LABELS.CONTACT_US_MESSAGE}{' '}
                    <Typography
                      component="a"
                      href={`tel:${LABELS.PHONE_NUMBER}`}
                      variant="body2"
                      color="primary"
                      fontWeight={500}
                      sx={{cursor: 'pointer', textDecoration: 'none'}}
                    >
                      {LABELS.PHONE_NUMBER}
                    </Typography>
                    .
                  </Typography>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h3">
                {LABELS.PRIVATE_INSURANCE_DETAILS}
              </Typography>
              <Typography sx={InsuranceDescription}>
                {LABELS.PRE_AUTH_CODE_MESSAGE}
              </Typography>
              <Box sx={insuranceDetailsBoxStyles}>
                <Box sx={selectInputBoxStyles}>
                  <SelectInput
                    name="insuranceId"
                    items={items as Insurance[]}
                    renderValue={renderValue}
                    placeholder={PLACEHOLDERS.INSURANCE_NAME}
                    renderItem={renderMenuItem}
                    value={
                      !isNaN(Number(values.insuranceId))
                        ? String(values.insuranceId)
                        : ''
                    }
                    onBlur={handleBlur}
                    onChange={handlePaymentMethodChange}
                    error={touched?.insuranceId && Boolean(errors?.insuranceId)}
                  />
                  {touched?.insuranceId && errors?.insuranceId && (
                    <Typography color="error" variant="caption">
                      {errors.insuranceId}
                    </Typography>
                  )}
                </Box>
                <Box sx={selectInputBoxStyles}>
                  <Input
                    name="membershipNumber"
                    placeholder={PLACEHOLDERS.MEMBERSHIP_NUMBER}
                    value={values.membershipNumber || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    touched={touched?.membershipNumber}
                    error={Boolean(errors?.membershipNumber)}
                    helperText={
                      touched?.membershipNumber && errors?.membershipNumber
                        ? errors?.membershipNumber
                        : ''
                    }
                  />
                </Box>
                <Box sx={selectInputBoxStyles}>
                  <Input
                    name="preAuthCode"
                    placeholder={PLACEHOLDERS.PRE_AUTH_CODE}
                    value={values.preAuthCode || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Box>
                <FormControlLabel
                  sx={{mt: 4}}
                  control={
                    <Checkbox
                      checked={values.acceptResponsibility}
                      onChange={e =>
                        setFieldValue('acceptResponsibility', e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {
                        LABELS.INSURANCE_DISCLAIMER.split(
                          LABELS.DOES_NOT_COVER
                        )[0]
                      }{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{color: 'primary.main'}}
                      >
                        {LABELS.DOES_NOT_COVER}
                      </Typography>
                      {
                        LABELS.INSURANCE_DISCLAIMER.split(
                          LABELS.DOES_NOT_COVER
                        )[1]
                      }
                    </Typography>
                  }
                />
              </Box>
            </>
          )}
        </Box>
      )}
    </>
  );
}

export default PatientInsuranceDetailForm;
